import { bool, mixed, number, object, string } from 'yup';

import { IS_AUTOCOMPLETE_ENABLED } from '@/constants';
import { validateAddressMatch } from '@/sections/address/validators/utils';
import type { Option } from '@/sections/shared/types/Option';

const PHONE_LENGTH_CHILEAN = 9;
const PHONE_MIN_LENGTH_INTERNATIONAL = 8;
const PHONE_MAX_LENGTH_INTERNATIONAL = 11;
const FIRST_NAME_MAX_LENGTH = 45;
const LAST_NAME_MAX_LENGTH = 45;
const STREET_NAME_MAX_LENGTH = 64;

// TODO: improve to move the validation for Address to module/domain
export const addressValidator = (
  placeData: { possibleRegion: string; possibleCommune: string } | null,
  regionOptions: Option[],
  communeOptions: Option[],
  isGuestUser: boolean
) =>
  object().shape({
    region: number()
      .notOneOf([0], 'Este campo es obligatorio.')
      .required('Este campo es obligatorio.'),
    commune: string().required('Este campo es obligatorio.'),
    streetName: string()
      .trim()
      .required('Este campo es obligatorio.')
      .when('isAutocomplete', {
        is: true,
        then: (schema) =>
          schema.test(
            'address-match',
            'La dirección ingresada no coincide con la región y comuna seleccionadas. Intenta ingresarla manualmente.',
            function (value) {
              if (
                !placeData ||
                !placeData.possibleRegion ||
                !placeData.possibleCommune
              ) {
                return true;
              }

              const { region, commune } = this.parent;
              return validateAddressMatch(
                { streetName: value || '', region, commune },
                placeData,
                regionOptions,
                communeOptions
              );
            }
          ),
        otherwise: (schema) =>
          schema.max(
            STREET_NAME_MAX_LENGTH,
            'Has llegado al máximo de caracteres.'
          )
      }),
    'no-number': bool().default(false).optional(),
    streetNumber: string()
      .trim()
      .when(['isAutocomplete', 'no-number'], {
        is: (isAutocomplete: boolean, noNumber: boolean) =>
          !isAutocomplete && !noNumber,
        then: (schema) => schema.required('Este campo es obligatorio.'),
        otherwise: (schema) =>
          schema.when('no-number', {
            is: true,
            then: (schema) => schema.optional(),
            otherwise: (schema) => schema.optional()
          })
      }),
    additionalStreetInfo: string().trim(),
    alias: isGuestUser
      ? string().trim().optional()
      : string().trim().required('Este campo es obligatorio.'),
    additionalAddressInfo: string().trim(),
    firstName: string()
      .trim()
      .required('Este campo es obligatorio.')
      .max(FIRST_NAME_MAX_LENGTH, 'Has llegado al máximo de caracteres.'),
    lastName: string()
      .trim()
      .required('Este campo es obligatorio.')
      .max(LAST_NAME_MAX_LENGTH, 'Has llegado al máximo de caracteres.'),
    phone: string()
      .trim()
      .required('Este campo es obligatorio.')
      .when('prefix-phone', {
        is: '+56',
        then: (schema) =>
          schema
            .length(PHONE_LENGTH_CHILEAN, 'El teléfono debe tener 9 dígitos.')
            .matches(
              /^9\d{8}$/,
              'Por favor, ingresa un número de teléfono válido.'
            ),
        otherwise: (schema) =>
          schema
            .min(
              PHONE_MIN_LENGTH_INTERNATIONAL,
              'El teléfono debe tener al menos 8 dígitos.'
            )
            .max(
              PHONE_MAX_LENGTH_INTERNATIONAL,
              'El teléfono no puede tener más de 11 dígitos.'
            )
      }),
    'prefix-phone': string().required('Este campo es obligatorio.'),
    'save-address': bool().default(false),
    isDefault: bool(),
    isAutocomplete: bool().default(IS_AUTOCOMPLETE_ENABLED).optional(),
    latitude: number().optional(),
    longitude: number().optional(),
    id: mixed<string | number>().optional()
  });
